/**
 * Object.entries produces a result of type `[string, V][]`, even if the input key
 * type is `number` or `symbol`. Here, we just cast to the correct type.
 */
export const entries = <K extends PropertyKey, V>(record: Partial<Record<K, V>>) =>
  Object.entries(record) as [K, V][];

export const keys = <T extends object>(record: T) => Object.keys(record) as (keyof T)[];

/**
 * Creates an object by associating keys and values derived from an array of elements.
 * Key-value pairs can optionally be filtered through a predicate function.
 *
 * @template T - The type of elements in the source array.
 * @template K - The type of the keys.
 * @template V - The type of the values.
 * @param {T[]} elements - An array of elements to process.
 * @param {(t: T) => K} keySelector - A function to extract keys from the elements.
 * @param {(t: T) => V} valueSelector - A function to extract values from the elements.
 * @param {(key: K, value: V) => boolean} [filter] - An optional function to filter key-value pairs.
 * @returns {Record<K, V>} An object where keys and values are derived from the elements.
 *
 * @example
 * const data = [{ id: 1, name: 'Alice' }, { id: 2, name: 'Bob' }];
 * const result = associate(data, item => item.id, item => item.name);
 * // Result: { 1: 'Alice', 2: 'Bob' }
 */
export const associate = <const T, K extends PropertyKey, V>(
  elements: readonly T[],
  keySelector: (t: T, index: number) => K,
  valueSelector: (t: T, index: number) => V,
  filter?: (key: K, value: V) => boolean,
): Record<K, V> =>
  Object.fromEntries(
    elements
      .map(
        (element, index) => [keySelector(element, index), valueSelector(element, index)] as [K, V],
      )
      .filter(([key, value]) => filter === undefined || filter(key, value)),
  ) as Record<K, V>;

/**
 * Associates elements from an array into an object by key without transforming the values.
 * Differs from the {@link associate} function by using the identity function as the value selector.
 *
 * @template T - The type of elements in the source array.
 * @template K - The type of the keys.
 * @param {T[]} elements - An array of elements to process.
 * @param {(t: T) => K} keyProvider - A function to extract keys from the elements.
 * @returns {Record<K, T>} An object where the keys are derived from the elements and the values are the elements themselves.
 *
 * @see {@link associate}
 */
export const associateBy = <T, K extends PropertyKey>(
  elements: T[],
  keyProvider: (t: T, index: number) => K,
): Record<K, T> => associate(elements, keyProvider, (value, index) => value);

/**
 * Maps an array of keys to an object using an association function and an optional filter.
 *
 * @template K Key type.
 * @template V Value type.
 * @param {K[]} keys Array of keys to associate values with.
 * @param {(value: K) => V} valueProvider Function generating values from keys.
 * @param {(entry: [K, V]) => boolean} [filter] Optional function for filtering key-value pairs.
 * @returns {Record<K, V>} An object associating keys with corresponding values.
 *
 * @example
 * const keys = ['a', 'b', 'c'];
 * const result = associateWith(keys, key => key.toUpperCase(), ([key, value]) => key !== 'b');
 * // result: { a: 'A', c: 'C' }
 */
export const associateWith = <const K extends PropertyKey, V>(
  keys: readonly K[],
  valueProvider: (value: K, index: number) => V,
  filter?: (key: K, value: V) => boolean,
): Record<K, V> =>
  associate(
    keys,
    (key) => key,
    (key, index) => valueProvider(key, index),
    filter,
  );

/**
 * A variant of {@link associateWith} that filters out null and undefined values.
 *
 * @see {@link associateWith}
 */
export const associateWithNotNull = <K extends PropertyKey, V>(
  keys: K[],
  association: (value: K) => V,
): Record<K, NonNullable<V>> =>
  associateWith(
    keys,
    association,
    (_, value) => value !== undefined && value !== null, //
  ) as Record<K, NonNullable<V>>;

/**
 * Partitions the properties of an object into a new object based on a provided key selector function.
 * The result is an object where the keys are derived from the selector function, and the values are sub-objects
 * containing the original key-value pairs that correspond to each partition key.
 *
 * @template K The type of the keys in the input object.
 * @template V The type of the values in the input object.
 * @template M The type of the partition keys.
 * @param {Record<K, V>} object The input object to be partitioned.
 * @param {(key: K, value: V) => M} partitionKeySelector A function to determine the partition key from a key-value pair.
 * @returns {Record<M, Record<K, V>>} A new object with partitioned sub-objects.
 *
 * @example
 * const data = { a: 1, b: 2, c: 3 };
 * const result = partitionBy(data, (key, value) => value % 2 === 0 ? 'even' : 'odd');
 * // result: { odd: { a: 1, c: 3 }, even: { b: 2 } }
 */
export const partitionBy = <K extends PropertyKey, V, M extends PropertyKey>(
  object: Partial<Record<K, V>>,
  partitionKeySelector: (key: K, value: V) => M,
): Record<M, Record<K, V>> => {
  const result = {} as Record<M, Record<K, V>>;

  for (const [key, value] of entries(object)) {
    const partitionKey = partitionKeySelector(key, value);

    if (!(partitionKey in result)) {
      result[partitionKey] = {} as Record<K, V>;
    }

    result[partitionKey][key] = value;
  }

  return result;
};

/**
 * Merges two objects by applying a value merge function to their corresponding keys.
 * Ensures all keys from both objects are included in the resulting object.
 *
 * @template K - The type of object keys.
 * @template V1 - The type of values in the first object.
 * @template V2 - The type of values in the second object.
 * @template R - The type of values in the resulting object.
 *
 * @param {Record<K, V1>} object1 - The first object to merge.
 * @param {Record<K, V2>} object2 - The second object to merge.
 * @param {function(V1 | undefined, V2 | undefined): R} valueMergeFunction - Function to merge values of corresponding keys.
 * @returns {Record<K, R>} The resulting object with merged values for all keys.
 *
 * @example
 * const obj1 = { a: 1, b: 2 };
 * const obj2 = { b: 3, c: 4 };
 * const result = mergeObjects(obj1, obj2, (val1 = 0, val2 = 0) => val1 + val2);
 * // result: { a: 1, b: 5, c: 4 }
 */
export const mergeObjects = <K extends PropertyKey, V1, V2, R>(
  object1: Partial<Record<K, V1>>,
  object2: Partial<Record<K, V2>>,
  valueMergeFunction: (value1?: V1, value2?: V2) => R,
): Record<K, R> => {
  const allKeys = keys(object1).concat(keys(object2));
  return Object.fromEntries(
    allKeys.map((key) => [key, valueMergeFunction(object1[key], object2[key])]),
  ) as Record<K, R>;
};

/**
 * Reverses the keys and values of the provided object.
 *
 * Takes an object with keys and values of type `K` and `V`,
 * and returns a new object with the values as keys and the keys as values.
 *
 * @template K, V
 * @param {Record<K, V>} object - The object to be reversed.
 * @returns {Record<V, K>} A new object with reversed key-value pairs.
 *
 * @example
 * const obj = { a: 1, b: 2, c: 3 };
 * const reversed = reverseObject(obj);
 * console.log(reversed); // { 1: 'a', 2: 'b', 3: 'c' }
 */
export const reverseObject = <K extends PropertyKey, V extends PropertyKey>(
  object: Record<K, V>,
): Record<V, K> =>
  Object.fromEntries(entries(object).map(([key, value]) => [value, key])) as Record<V, K>;

/**
 * Maps the keys of an object using a provided function while keeping the values unchanged.
 *
 * @template K1 - The type of the original keys.
 * @template K2 - The type of the transformed keys.
 * @template V - The type of the object values.
 * @param {Record<K1, V>} object - The input object whose keys are to be transformed.
 * @param {(key: K1, value: V) => K2} keyMapper - A function to transform each key.
 * @returns {Record<K2, V>} An object with transformed keys and unchanged values.
 *
 * @example
 * const obj = { a: 1, b: 2 };
 * const newKeys = mapKeys(obj, (key) => key.toUpperCase());
 * // Result: { A: 1, B: 2 }
 */
export const mapKeys = <K1 extends PropertyKey, K2 extends PropertyKey, V>(
  object: Partial<Record<K1, V>>,
  keyMapper: (key: K1, value: V) => K2,
): Record<K2, V> =>
  Object.fromEntries(
    entries(object).map<[K2, V]>(([key, value]) => [keyMapper(key, value), value]),
  ) as Record<K2, V>;

/**
 * Transforms an object's values by applying a provided mapping function to each value.
 * The keys remain unchanged, producing a new object with the same keys and modified values.
 *
 * @template K - The type of the keys in the object.
 * @template V1 - The type of the original values in the object.
 * @template V2 - The type of the resulting values after mapping.
 * @param {Record<K, V1>} object - An object containing the key-value pairs to transform.
 * @param {(key: K, value: V1) => V2} valueMapper - A function that receives each key and value
 * and returns the transformed value.
 * @returns {Record<K, V2>} A new object with the same keys and transformed values.
 *
 * @example
 * const input = { a: 1, b: 2 };
 * const result = mapValues(input, (key, value) => value * 2);
 * console.log(result); // { a: 2, b: 4 }
 */
export const mapValues = <K extends PropertyKey, V1, V2>(
  object: Partial<Record<K, V1>>,
  valueMapper: (key: K, value: V1) => V2,
): Record<K, V2> =>
  Object.fromEntries(
    entries(object).map<[K, V2]>(([key, value]) => [key, valueMapper(key, value)]),
  ) as Record<K, V2>;

/**
 * Groups elements of an array based on a derived key and transforms the grouped values.
 *
 * @template T The type of the input elements.
 * @template K The type of the derived key.
 * @template V The type of the transformed output values.
 * @param {T[]} elements The array of elements to be grouped.
 * @param {(t: T) => K} keySelector Function to derive the grouping key for each element.
 * @param {(t: T) => V} valueTransform Function to transform each element into grouped values.
 * @returns {Record<K, V[]>} An object where each key corresponds to the derived grouping key
 *  and each value is an array of transformed values.
 *
 * @example
 * const data = [{ id: 1, value: 'a' }, { id: 1, value: 'b' }, { id: 2, value: 'c' }];
 * const group = groupBy(data, item => item.id, item => item.value);
 * // Result: { 1: ['a', 'b'], 2: ['c'] }
 */
export const groupBy = <T, K extends PropertyKey, V>(
  elements: T[],
  keySelector: (t: T) => K,
  valueTransform: (t: T) => V,
): Record<K, V[]> =>
  elements.reduce(
    (result, element) => {
      const key = keySelector(element);
      const value = valueTransform(element);

      result[key] = (result[key] || []).concat(value);
      return result;
    },
    {} as Record<K, V[]>,
  );

export const removeUndefinedProperties = <K extends PropertyKey, V>(
  object: Record<K, V>,
): Record<K, NonNullable<V>> =>
  Object.fromEntries(
    entries(object).filter(([_, value]) => value !== undefined && value !== null),
  ) as Record<K, NonNullable<V>>;

export const simpleEquals = <K extends PropertyKey, V>(
  object1: Record<K, V>,
  object2: Record<K, V>,
) => {
  if (keys(object1).length !== keys(object2).length) return false;
  return keys(object1).every((key) => object1[key] === object2[key]);
};

export const filterObject = <K extends PropertyKey, V>(
  object: Record<K, V>,
  predicate: (key: K, value: V) => boolean,
): Partial<Record<K, V>> =>
  Object.fromEntries(
    entries(object).filter(([key, value]) => predicate(key, value as V)),
  ) as Record<K, V>;
